import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getTheme, createThemeObject } from '../theme/themes';
import { AuthContext } from './AuthContext';
import { useUpdateAppSettings } from '../hooks/useBackend';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const { updateAppSettings } = useUpdateAppSettings();
  
  // Get initial theme from user settings or default to 'dark'
  const getInitialTheme = () => {
    try {
      // Check user settings first
      if (user?.appSettings?.theme) {
        return user.appSettings.theme;
      }
      // Check localStorage as fallback
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme === 'light' || savedTheme === 'dark') {
        return savedTheme;
      }
    } catch (e) {
      console.error('Error loading theme:', e);
    }
    return 'dark'; // Default theme
  };

  const [themeName, setThemeName] = useState(getInitialTheme);
  const [colors, setColors] = useState(() => getTheme(getInitialTheme()));
  const [theme, setTheme] = useState(() => createThemeObject(getTheme(getInitialTheme())));

  // Update CSS variables when theme changes
  const updateCSSVariables = useCallback((themeColors, theme) => {
    const root = document.documentElement;
    
    // Set data-theme attribute for CSS selectors
    root.setAttribute('data-theme', theme);
    
    // Update all CSS variables
    Object.entries(themeColors).forEach(([key, value]) => {
      // Convert camelCase to kebab-case for CSS variables
      const cssVarName = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      root.style.setProperty(`--${cssVarName}`, value);
      
      // Also set some common aliases for backwards compatibility
      if (key === 'bgPrimary') {
        root.style.setProperty('--dark-bg-primary', value);
      }
      if (key === 'bgSecondary') {
        root.style.setProperty('--dark-bg-secondary', value);
      }
      if (key === 'bgHeader') {
        root.style.setProperty('--dark-bg-header', value);
      }
      if (key === 'border') {
        root.style.setProperty('--dark-border', value);
      }
      if (key === 'borderDark') {
        root.style.setProperty('--border-dark', value);
      }
      if (key === 'text') {
        root.style.setProperty('--dark-text', value);
      }
    });

    // Update body background
    document.body.style.backgroundColor = themeColors.bgPrimary;
    document.body.style.color = themeColors.text;
  }, []);

  // Switch theme function
  const switchTheme = useCallback(async (newTheme) => {
    const themeToUse = newTheme === 'light' ? 'light' : 'dark';
    
    try {
      setThemeName(themeToUse);
      const newColors = getTheme(themeToUse);
      setColors(newColors);
      setTheme(createThemeObject(newColors));
      
      // Update CSS variables
      updateCSSVariables(newColors, themeToUse);
      
      // Save to localStorage
      localStorage.setItem('theme', themeToUse);
      
      // Save to backend if user is logged in
      if (user?.token) {
        try {
          await updateAppSettings(user.token, { theme: themeToUse });
        } catch (error) {
          console.error('Error saving theme to backend:', error);
        }
      }
    } catch (error) {
      console.error('Error switching theme:', error);
    }
  }, [user, updateAppSettings, updateCSSVariables]);

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    const newTheme = themeName === 'dark' ? 'light' : 'dark';
    switchTheme(newTheme);
  }, [themeName, switchTheme]);

  // Initialize CSS variables and sync with user settings
  useEffect(() => {
    // Update CSS variables on mount and when theme changes
    updateCSSVariables(colors, themeName);
  }, [colors, themeName, updateCSSVariables]);

  // Sync theme with user settings when user changes
  useEffect(() => {
    if (user?.appSettings?.theme) {
      const userTheme = user.appSettings.theme;
      if (userTheme !== themeName) {
        const newColors = getTheme(userTheme);
        setThemeName(userTheme);
        setColors(newColors);
        setTheme(createThemeObject(newColors));
        updateCSSVariables(newColors);
        localStorage.setItem('theme', userTheme);
      }
    }
  }, [user?.appSettings?.theme]); // Only depend on theme property

  const value = {
    themeName,
    theme,
    colors,
    switchTheme,
    toggleTheme,
    isDark: themeName === 'dark',
    isLight: themeName === 'light',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;




